import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  LButton,
  LGrid,
  LFade,
  LNotification,
  LNotificationGroup,
  LSwitch,
  LDialog
} from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AuthContext } from "../../../../../context/AuthContext";
import { ProductForm } from "../ProductForm/ProductForm";
import { CPConfigurationManagement } from "../../ClientManagement/CTable/CProductManagement/CPConfigManagement/CPConfigManagement";
import styles from "./ProductTable.module.css";
import KeyBasedLButton from "../../../../Shared/KeyBasedLButton"
import LoadingOverlay from "src/components/Shared/LoadingOverlay/LoadingOverlay";
import { GabSyncManagement } from "../../GabSyncManagement/GabSyncManagement";
import { useGridScrollGuard } from "src/helpers/useGridScrollGuard";

const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;

// Seeded "Prime Broker Integrated Solution - GAB" product type. GAB Sync is offered only for the
// non-multitenant GAB product (a single database).
const GAB_PRODUCT_TYPE_ID = "8f63142d-4d0c-4145-9a96-62d389533f1c";
// "PBIN GAB Settings" permission configuration type. This sync is PBIN-specific, so the
// product must carry this permission type in addition to the GAB product type.
const PBIN_GAB_PERMISSION_TYPE_ID = 4;

// GAB Sync is offered only for the non-multitenant, PBIN GAB product: it has a single database
// and this sync only understands PBIN GAB, identified by the "PBIN GAB Settings" permission
// configuration type. Module scope (pure) so memoized columns can reference it stably.
const isGabSyncProduct = (dataItem) =>
  !dataItem.isMultiTenant &&
  (dataItem.productTypeId || "").toLowerCase() === GAB_PRODUCT_TYPE_ID &&
  Number(dataItem.permissionConfigurationTypeId) === PBIN_GAB_PERMISSION_TYPE_ID;

function ProductTable(props) {
  const context = useContext(AuthContext);

  const [state, setState] = useState({
    authLoaded: false,
    displayConfiguration: false,
    displayProductForm: false,
    displayProductFormSaveSuccess: false,
    selectedProduct: {},
    selectedProductGuid: {},
    rebindStatus: true,
    productsLoaded: false,
    displaySync: false,
    syncProduct: null
  });
  const [isLoading, setIsLoading] = useState(false);

  const [headers, setHeaders] = useState(null);
  const [products, setProducts] = useState([]);
  const loadingPanel = <LoadingOverlay loadingText="Loading"/>

  let getAllProducts = useCallback(async (e) => {
    setIsLoading(true);
    let authContext = context;
    let headers = {
      Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
      "Content-Type": "application/json",
    };

    let init = { mode: "cors", headers: headers };
    let url = GATEWAY_BASE + '/multitenancy/api/Products';

    await fetch(url, init)
      .then(response => response.json())
      .then(json => {
        setProducts(json);
        setIsLoading(false);
      });
  }, [setIsLoading, setProducts, context]);


  useEffect(() => {
    if (state.rebindStatus === true) {
      setState(s => ({ ...s, rebindStatus: false, productsLoaded: true }))
    }
  }, [products, state.rebindStatus]);

  useEffect(() => {
    if (state.headersLoaded === true) {
      if ((products.length === 0) || state.rebindStatus) {
        getAllProducts();
      }
    }

  }, [state.headersLoaded, state.rebindStatus, products, getAllProducts]);

  useEffect(() => {
    let authContext = context;

    let checkForUser = setInterval(() => {
      if (authContext.isLoaded() && authContext.authState()) {
        setHeaders({
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
        });

        setState(s => ({
          ...s,
          authLoaded: true,
        }));

        clearInterval(checkForUser);
      }
    }, 100);
  }, [context]);

  useEffect(() => {
    if (headers !== null && typeof (headers['Authorization']) != 'undefined') {
      setState(s => ({
        ...s,
        authLoaded: true,
        headersLoaded: true
      }));
    }
  }, [headers]);

  const displayProductForm = (productGuid, display) => {
    let authContext = context; 

    if (productGuid) {
      fetch(GATEWAY_BASE + "/multitenancy/api/products/" + productGuid, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
        },
      })
        .then((res) => res.json())
        .then(
          (result) => {
            setState({
              ...state,
              displayProductForm: display,
              selectedProduct: {
                name: result.name,
                subName: result.subName,
                description: result.description,
                isActive: result.isActive,
                url: result.url,
                version: result.version,
                productLogoFileName: result.productLogoFileName,
                releaseDate: result.releaseDate
                  ? new Date(result.releaseDate)
                  : null,
                productGuid: result.productGuid,
                permissionConfigurationTypeId:
                  result.permissionConfigurationTypeId,
                isAdminOnly: result.isAdminOnly,
                isMultiTenant: result.isMultiTenant,
                productTypeId: result.productTypeId
              },
            });
          },
          (error) => {
            setState({
              ...state,
              isLoaded: false,
              error,
            });
          }
        );
    } else {
      setState({
        ...state,
        displayProductForm: display,
        selectedProduct: {},
      });
    }
  };

  const afterSave = () => {
    setState({
      ...state,
      displayProductFormSaveSuccess: true,
      displayProductForm: false,
      rebindStatus: true,
      productsLoaded: false
    });
  };

  const afterConfigSave = () => {
    setState({
      ...state,
      displayProductFormSaveSuccess: true,
      displayConfiguration: false,
      rebindStatus: true,
      productsLoaded: false
    });
  };

  const rebind = () => {
    getAllProducts();
  };


  const toggleClientProduct = (e, selectedProduct) => {
    let authContext = context;
    let url = GATEWAY_BASE + "/multitenancy/api/Products";
    let product = selectedProduct;

    product.isActive = !selectedProduct.isActive;
    product.lastUpdatedBy = authContext.userInfo().loffaUserGuid;

    fetch(url, {
      method: "PATCH",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(product),
    })
      .then(function (response) {
        if (response.ok) {
          rebind();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const displayConfigurationForm = (productGuid, display) => {
    if (display) {
      setState({
        ...state, selectedProductGuid: productGuid, displayConfiguration: true
      });
    }
    else {
      setState({
        ...state, selectedProductGuid: null, displayConfiguration: false
      });
    }
  };

  const openSync = (productGuid, productName) => {
    setState({ ...state, displaySync: true, syncProduct: { guid: productGuid, name: productName } });
  };

  const closeSync = () => {
    setState({ ...state, displaySync: false, syncProduct: null });
  };

  // GAB Sync opens as a full-screen overlay; Escape closes it non-destructively (the server run is
  // left intact so it can be resumed — "Discard" inside the flow is the destructive exit).
  useEffect(() => {
    if (!state.displaySync) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setState((s) => ({ ...s, displaySync: false, syncProduct: null }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.displaySync]);

  // Keep the latest handlers in a ref so the column definitions can be built once and stay
  // referentially stable. Rebuilding `columns` on every render (opening a dialog, toggling a
  // switch) hands the Kendo grid a new column set, which makes it re-initialise and snap its
  // scroll back to the top. Cells call the current handlers via this ref so they never go stale.
  const cellHandlers = useRef({});
  cellHandlers.current = {
    displayProductForm,
    displayConfigurationForm,
    openSync,
    toggleClientProduct,
  };

  const productColumns = useMemo(() => {
    const dateCell = (value) => {
      var formattedDate = value.dataItem.releaseDate
        ? new Date(value.dataItem.releaseDate)
        : null;
      return (
        <td>
          {formattedDate &&
            formattedDate.getMonth() +
            "/" +
            formattedDate.getDate() +
            "/" +
            formattedDate.getFullYear()}
        </td>
      );
    };

    const editCell = (value) => {
      return (
        <td>
          <KeyBasedLButton
            className="green"
            themeColor="primary"
            onClick={() =>
              cellHandlers.current.displayProductForm(value.dataItem.productGuid, true)
            }
            buttonText="Edit"
            parentKey={value.dataItem.productGuid}
          >
          </KeyBasedLButton>
        </td>
      );
    };

    const configureCell = (value) => {
      return (
        <td className={styles.configurationCell}>
          <div className={styles.configActions}>
          {!value.dataItem.isMultiTenant && (
            <LButton
              className="green"
              themeColor="primary"
              onClick={() =>
                cellHandlers.current.displayConfigurationForm(value.dataItem.productGuid, true)
              }
            >
              Configure
            </LButton>
          )}
          {isGabSyncProduct(value.dataItem) && (
            <LButton
              look="flat"
              onClick={() =>
                cellHandlers.current.openSync(value.dataItem.productGuid, value.dataItem.name)
              }
              tooltip="GAB Sync — review and merge Prod GAB data into this environment"
              tooltipPosition="bottomRight"
            >
              <FontAwesomeIcon icon="fa-solid fa-code-compare" /> Sync
            </LButton>
          )}
          {value.dataItem.isMultiTenant && (
            <LButton
              className={styles.tooltipPosition}
              look="flat"
              onClick={null}
              tooltip="This product has multi-tenancy enabled. Please configure product through Client Management."
              tooltipPosition="bottomRight"
            ><FontAwesomeIcon icon="info-circle" /> </LButton>
          )}
          </div>
        </td>
      );
    };

    const statusCell = (value) => {
      return (
        <td>
          <div>
            {value.dataItem.isActive !== null && (
              <LSwitch
                width={100}
                id={value.dataItem.productGuid}
                onChange={(e) => cellHandlers.current.toggleClientProduct(e, value.dataItem)}
                onLabel={"Enabled"}
                offLabel={"Disabled"}
                defaultChecked={value.dataItem.isActive}
              ></LSwitch>
            )}
          </div>
        </td>
      );
    };

    return [
      { field: "name", title: "Name", width: "300px" },
      { field: "subName", title: "Sub Name", width: "200px" },
      { field: "url", title: "URL", width: "200px" },
      {
        field: "description",
        title: "Description",
        width: "300px",
        filterable: false,
      },
      {
        field: "releaseDate",
        title: "Release Date",
        cell: dateCell,
        width: "120px",
        filterable: false,
      },
      {
        field: "version",
        title: "Version",
        width: "150px",
        filterable: false,
      },
      {
        field: "isActive",
        title: "Status",
        cell: statusCell,
        width: "120px",
        filterable: false,
      },
      {
        field: "productGuid",
        title: "Details",
        cell: editCell,
        filterable: false,
        sortable: false,
        width: "100px",
      },
      {
        field: "productGuid",
        title: "Configuration",
        cell: configureCell,
        filterable: false,
        sortable: false,
        width: "220px",
      },
      {
        field: "productGuid",
        title: "Product GUID",
        width: "250px",
        filterable: false,
      },
    ];
  }, []);

  // Memoise the grid element itself so that re-rendering this component (opening a dialog,
  // toggling state, showing a notification) does NOT re-render the Kendo grid — that re-render
  // is what resets its scroll to the top. `products` and `productColumns` are both stable, so
  // React reuses the exact same element and skips the grid entirely until the data changes.
  const gridElement = useMemo(
    () => (
      <LGrid
        style={{ height: "500px" }}
        data={products}
        sortable
        filterable
        pageable
        columns={productColumns}
      ></LGrid>
    ),
    [products, productColumns]
  );

  // Works around the @loffa LGrid scroll-jump (clicking a cell resets the grid/page scroll).
  const gridWrapRef = useRef(null);
  useGridScrollGuard(gridWrapRef);

  return (
    <div className="relative-div">
      {isLoading && loadingPanel}
      {state.authLoaded ? (
        <div>
          <div className={styles.productManagementActions}>
            <LButton
              themeColor="primary"
              className="green"
              onClick={() => displayProductForm(null, true)}
            >
              <FontAwesomeIcon icon="plus" /> Add New Product
            </LButton>
          </div>
          {!state.productsLoaded && loadingPanel}
          <div ref={gridWrapRef}>{gridElement}</div>
          <LNotificationGroup>
            <LFade>
              {state.displayProductFormSaveSuccess && (
                <LNotification
                  type={{ style: "success", icon: true }}
                  closable={true}
                  onClose={() =>
                    setState({ displayProductFormSaveSuccess: false })
                  }
                >
                  <span>Product saved.</span>
                </LNotification>
              )}
            </LFade>
          </LNotificationGroup>
          <LFade transitionEnterDuration={200}>
            {state.displayProductForm && (
              <ProductForm
                selectedProduct={state.selectedProduct}
                closeForm={() => displayProductForm(null, false)}
                saveForm={() => afterSave()}
              ></ProductForm>
            )}
          </LFade>
          <LFade transitionEnterDuration={200}>
            {state.displayConfiguration && (
              <LDialog
                className={styles.configurationDialog}
                key={props.productGuid}
                width="800"
                title="Configure Product"
                body={
                  <CPConfigurationManagement
                    productGuid={state.selectedProductGuid}
                    clientGuid={null}
                    closeForm={() => displayConfigurationForm(null, false)}
                    saveSuccessForm={() => afterConfigSave(null, false)}
                  ></CPConfigurationManagement>
                }
                actionCloseButton={() => displayConfigurationForm(null, false)}
                actionOverlay={() => displayConfigurationForm(null, false)}
              />
            )}
          </LFade>
          {state.displaySync && state.syncProduct && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                background: "#eef1f4",
                overflow: "hidden",
              }}
              role="dialog"
              aria-modal="true"
            >
              <GabSyncManagement
                productGuid={state.syncProduct.guid}
                productName={state.syncProduct.name}
                onClose={closeSync}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export { ProductTable };
